"use client";

import { forwardRef, useCallback, useState, useEffect, useMemo } from "react";
import { CalendarIcon } from "lucide-react";
import { ControllerRenderProps, FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { TIME_FORMAT, MIN_PROJECT_DATE, DATETIME_FORMAT } from "@/constants";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { cn } from "@/lib/utils";
import { DayPickerProps } from "react-day-picker";
import { useLocale } from "@/hooks/useLocale";

export const DateTimePicker = forwardRef<
  HTMLInputElement,
  ControllerRenderProps<FieldValues, string> & {
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    datePickerProps?: Omit<
      DayPickerProps,
      "mode" | "selected" | "onSelect" | "captionLayout"
    >;
  }
>(function DateTimePicker(
  {
    onChange,
    onBlur,
    value,
    placeholder,
    className,
    disabled,
    datePickerProps = {},
    ...props
  },
  ref,
) {
  const { t } = useTranslation();
  const locale = useLocale();
  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTime, setSelectedTime] = useState<string>("00:00");

  useEffect(() => {
    if (value) {
      const dt = DateTime.fromISO(value);
      if (dt.isValid) {
        const localDt = dt.toLocal();
        setSelectedDate(localDt.toJSDate());
        setSelectedTime(localDt.toFormat(TIME_FORMAT));
      }
    }
  }, [value]);

  const onChangeInternal = useCallback(
    (dt: DateTime) => {
      onChange(dt.toISO());
    },
    [onChange],
  );

  const handleDateSelect = useCallback(
    (date: Date | undefined) => {
      if (date) {
        setSelectedDate(date);
        const dt = DateTime.fromJSDate(date).set({
          hour: parseInt(selectedTime.split(":")[0]),
          minute: parseInt(selectedTime.split(":")[1]),
        });
        onChangeInternal(dt);
      }
    },
    [selectedTime, onChangeInternal],
  );

  const handleTimeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const time = e.target.value;
      setSelectedTime(time);

      if (selectedDate) {
        const [hours, minutes] = time.split(":").map(Number);
        const dt = DateTime.fromJSDate(selectedDate).set({
          hour: hours,
          minute: minutes,
        });
        onChangeInternal(dt);
      }
    },
    [selectedDate, onChangeInternal],
  );

  const handleApply = useCallback(() => {
    setOpen(false);
    onBlur?.();
  }, [onBlur]);

  const displayValue = useMemo(
    () =>
      selectedDate && selectedTime
        ? DateTime.fromJSDate(selectedDate)
            .set({
              hour: parseInt(selectedTime.split(":")[0]),
              minute: parseInt(selectedTime.split(":")[1]),
            })
            .toFormat(DATETIME_FORMAT)
        : "",
    [selectedDate, selectedTime],
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "justify-start text-left font-normal w-42 h-8 text-xs",
            !displayValue && "text-muted-foreground",
            className,
          )}
        >
          <CalendarIcon className="h-4 w-4" />
          {displayValue || placeholder || t("datePicker.selectDateTime")}
        </Button>
      </DialogTrigger>
      <DialogContent className="w-auto p-0 max-w-sm">
        <VisuallyHidden>
          <DialogTitle>{t("datePicker.selectDateTime")}</DialogTitle>
        </VisuallyHidden>
        <div className="flex flex-col">
          <div className="p-4">
            <Calendar
              disabled={(date) => date < MIN_PROJECT_DATE}
              {...datePickerProps}
              mode="single"
              selected={selectedDate}
              onSelect={handleDateSelect}
              defaultMonth={datePickerProps?.defaultMonth ?? selectedDate}
              captionLayout="dropdown"
              className="w-full"
              startMonth={datePickerProps?.startMonth ?? MIN_PROJECT_DATE}
              locale={locale}
            />
          </div>
          <div className="border-t bg-background p-4">
            <Label htmlFor="time" className="text-sm font-medium mb-2 block">
              {t("datePicker.time")}
            </Label>
            <div className="flex gap-2">
              <Input
                id="time"
                type="time"
                value={selectedTime}
                onChange={handleTimeChange}
                onBlur={onBlur}
                className="flex-1"
              />
              <Button onClick={handleApply} disabled={!selectedDate} size="sm">
                {t("datePicker.apply")}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
      <input type="hidden" value={value || ""} {...props} ref={ref} />
    </Dialog>
  );
});

DateTimePicker.displayName = "DateTimePicker";

export default DateTimePicker;
